import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getShopNotificationConsentQuery } from '@/api/booking/getShopNotificationConsentQuery';
import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import type { UserInformationFormValues } from '@/components/_shared/UserInformationForm/types';
import type { BookingConsentSheetProps } from '@/components/booking/BookingConsentSheet/types';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

interface UseStandbyWaitlistConsentParams {
  shopId: string;
  join: (details: UserInformationFormValues) => void;
  clearConsent: () => void;
}

export const useStandbyWaitlistConsent = ({
  shopId,
  join,
  clearConsent,
}: UseStandbyWaitlistConsentParams) => {
  const { i18n } = useScreenTranslations('ChooseTimeScreen');
  const queryClient = useQueryClient();
  const consentSheetRef = useRef<BottomSheetRef>(null);
  const detailsRef = useRef<UserInformationFormValues | null>(null);
  const isLoadingRef = useRef(false);
  const [consentCustomer, setConsentCustomer] =
    useState<BookingConsentSheetProps['customer']>(null);
  const [isConsentReady, setIsConsentReady] = useState(false);
  const [isReadingConsent, setIsReadingConsent] = useState(false);

  // Opened from an effect so the sheet has rendered with this customer's
  // consent read: its open() skips answered channels, or joins if none remain.
  useEffect(() => {
    if (!isConsentReady) return;
    setIsConsentReady(false);
    consentSheetRef.current?.open();
  }, [isConsentReady]);

  const askConsent = useCallback(
    (details: UserInformationFormValues) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      setIsReadingConsent(true);
      clearConsent();
      detailsRef.current = details;
      const customer = { ...details, preferredLanguage: i18n.language };
      setConsentCustomer(customer);
      // A failed read still opens the sheet, which then shows every channel.
      queryClient
        .fetchQuery(
          getShopNotificationConsentQuery({ shopId, customer, retry: false }),
        )
        .catch(() => undefined)
        .finally(() => {
          isLoadingRef.current = false;
          setIsReadingConsent(false);
          setIsConsentReady(true);
        });
    },
    [clearConsent, i18n.language, queryClient, shopId],
  );

  const joinAfterConsent = useCallback(() => {
    if (detailsRef.current) join(detailsRef.current);
  }, [join]);

  return {
    consentSheetRef,
    consentCustomer,
    isReadingConsent,
    askConsent,
    joinAfterConsent,
  };
};
